import React, { useState } from 'react';
import { useSnackbar, closeSnackbar, SnackbarKey } from 'notistack';
import { Button } from '@mui/material';
import {
  Assignee,
  Ticket,
  TicketStatus,
} from '@marageti/z4-sdk/lib/tickets';
import { useApiClient } from '@marageti/z4-lib';
import { EditableTicket, TicketFormData } from '../pages/tickets/options';
import { TicketCloseFormData } from '../components/modals/ticket-close-modal';
import { TicketReassignFormData } from '../components/modals/ticket-reassign-modal';

const viewTicketAction = (ticketId: string) => (key: SnackbarKey) => (
  <Button
    href={`/tickets/${ticketId}`}
    color="inherit"
    onClick={() => closeSnackbar(key)}
  >
    View
  </Button>
);

const useTicketListActions = (refetch: () => void) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();

  const [isModalOpen, setModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<EditableTicket | undefined>(undefined);

  const [isCloseModalOpen, setCloseModalOpen] = useState(false);
  const [closingTicketId, setClosingTicketId] = useState<string | undefined>(undefined);

  const [isReassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassigningTicket, setReassigningTicket] = useState<Ticket | undefined>(undefined);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTicketId, setDeletingTicketId] = useState<string | undefined>(undefined);

  const handleStatusChange = async (ticketId: string, newStatus: TicketStatus) => {
    if (newStatus === 'COMPLETED') {
      setClosingTicketId(ticketId);
      setCloseModalOpen(true);
      return;
    }

    try {
      await apiClient.ticketsClient.updateTicketStatus(ticketId, { status: newStatus });
      enqueueSnackbar('Ticket status updated.', { variant: 'success' });
      await Promise.resolve(refetch());
    } catch {
      enqueueSnackbar('Failed to update ticket status.', { variant: 'default' });
    }
  };

  const handleOpenCreate = () => {
    setEditingTicket(undefined);
    setModalOpen(true);
  };

  const handleEditTicket = (ticket: Ticket) => {
    setEditingTicket({
      id: ticket.id,
      ticketType: ticket.ticketType,
      ticketSubType: ticket.ticketSubType,
      status: ticket.status,
      title: ticket.title,
      description: ticket.description,
      travelerId: ticket.travelerId,
      travelerName: ticket.travelerName,
      tripRequestId: ticket.tripRequestId,
      tripMatchId: ticket.tripMatchId,
      assignees: ticket.assignees || [],
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTicket(undefined);
  };

  const handleSaveTicket = async (data: TicketFormData) => {
    const ticketAssignees: Assignee[] = data.assignees.map((a) => ({
      id: a.value,
      name: a.title,
    }));

    try {
      if (editingTicket) {
        await apiClient.ticketsClient.updateTicket(editingTicket.id, {
          title: data.title,
          description: data.description,
          assignees: ticketAssignees,
          ticketSubType: data.type?.value,
          status: data.status.value,
          travelerId: data.traveler?.id || '',
          travelerName: data.traveler?.fullName || '',
          tripRequestId: data.tripRequest?.value,
          tripMatchId: data.tripMatch?.value || '',
        });
        enqueueSnackbar('Ticket updated.', {
          variant: 'success',
          action: viewTicketAction(editingTicket.id),
        });
      } else {
        const created = await apiClient.ticketsClient.createTicket({
          title: data.title,
          description: data.description,
          ticketType: data.type!.ticketType,
          ticketSubType: data.type!.value,
          status: data.status.value,
          travelerId: data.traveler?.id || '',
          travelerName: data.traveler?.fullName || '',
          assignees: ticketAssignees,
          tripRequestId: data.tripRequest?.value,
          tripMatchId: data.tripMatch?.value || '',
        });
        enqueueSnackbar('Ticket created.', {
          variant: 'success',
          action: viewTicketAction(created.id),
        });
      }
      handleCloseModal();
      refetch();
    } catch {
      enqueueSnackbar(
        editingTicket ? 'Failed to update ticket.' : 'Failed to create ticket.',
        { variant: 'default' },
      );
    }
  };

  const handleCloseCloseModal = () => {
    setCloseModalOpen(false);
    setClosingTicketId(undefined);
  };

  const handleSaveClose = async (data: TicketCloseFormData) => {
    if (!closingTicketId) return;
    try {
      await apiClient.ticketsClient.updateTicketStatus(closingTicketId, {
        status: 'COMPLETED',
        outcomeSeverity: data.outcomeSeverity?.value,
        outcomeComments: data.outcomeComments,
        ...(data.zicassoCreditEnabled && {
          zicassoCreditAmount: parseFloat(data.zicassoCreditAmount),
        }),
        ...(data.partnerRefundEnabled && {
          partnerRefundAmount: parseFloat(data.partnerRefundAmount),
        }),
      });
      enqueueSnackbar('Ticket closed.', {
        variant: 'success',
        action: viewTicketAction(closingTicketId),
      });
      handleCloseCloseModal();
      refetch();
    } catch {
      enqueueSnackbar('Failed to close ticket.', { variant: 'default' });
    }
  };

  const handleReassignTicket = (ticket: Ticket) => {
    setReassigningTicket(ticket);
    setReassignModalOpen(true);
  };

  const handleCloseReassignModal = () => {
    setReassignModalOpen(false);
    setReassigningTicket(undefined);
  };

  const handleSaveReassign = async (data: TicketReassignFormData) => {
    if (!reassigningTicket) return;
    const ticketAssignees: Assignee[] = data.assignees.map((a) => ({
      id: a.value,
      name: a.title,
    }));
    try {
      await apiClient.ticketsClient.updateTicket(reassigningTicket.id, {
        title: reassigningTicket.title,
        description: reassigningTicket.description,
        status: reassigningTicket.status,
        ticketSubType: reassigningTicket.ticketSubType,
        travelerId: reassigningTicket.travelerId,
        travelerName: reassigningTicket.travelerName,
        tripRequestId: reassigningTicket.tripRequestId,
        tripMatchId: reassigningTicket.tripMatchId,
        assignees: ticketAssignees,
      });
      enqueueSnackbar('Ticket updated.', {
        variant: 'success',
        action: viewTicketAction(reassigningTicket.id),
      });
      handleCloseReassignModal();
      refetch();
    } catch {
      enqueueSnackbar('Failed to reassign ticket.', { variant: 'default' });
    }
  };

  const handleDeleteTicket = (ticket: Ticket) => {
    setDeletingTicketId(ticket.id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletingTicketId(undefined);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTicketId) return;
    try {
      await apiClient.ticketsClient.updateTicketStatus(deletingTicketId, { status: 'CANCELED' });
      enqueueSnackbar('Ticket deleted.', { variant: 'success' });
      handleCloseDeleteModal();
      refetch();
    } catch {
      enqueueSnackbar('Failed to delete ticket.', { variant: 'default' });
    }
  };

  return {
    isModalOpen,
    editingTicket,
    handleOpenCreate,
    handleEditTicket,
    handleCloseModal,
    handleSaveTicket,
    isCloseModalOpen,
    handleCloseCloseModal,
    handleSaveClose,
    isReassignModalOpen,
    reassigningTicketAssigneeIds: reassigningTicket?.assignees?.map((a) => a.id) || [],
    handleReassignTicket,
    handleCloseReassignModal,
    handleSaveReassign,
    isDeleteModalOpen,
    handleDeleteTicket,
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleStatusChange,
  };
};

export default useTicketListActions;
